/**
 * PLANS array — CLAUDE.md §4.6 backend ↔ frontend mapping.
 * Backend SubscriptionPlan enum int = serverId.
 */
export type PlanId = 'free' | 'starter' | 'gold';

// Зөвхөн одоо хийгдсэн, багцад санал болгож буй модулиуд. Хийгдээгүй feature
// (custom тайлан, public API, тэргүүлэх дэмжлэг г.м.) -ийг зориуд хассан.
export type FeatureCode =
  | 'pos'
  | 'inventory.basic'
  | 'inventory.purchase'
  | 'inventory.sale'
  | 'inventory.stockcount'
  | 'report.basic'
  | 'report.full'
  | 'ebarimt'
  | 'ar'
  | 'ap'
  | 'multilocation'
  | 'support.chat'
  | 'shop.b2b';

export interface PlanQuotas {
  maxCompanies: number;
  maxLocations: number;
  maxUsers: number;
  maxItems: number;
  maxPosTxnsPerMonth: number;
  auditRetentionDays: number;
  trialDays?: number;
}

export interface PlanDef {
  id: PlanId;
  serverId: number;
  label: string;
  color: string;
  priceMonthly: number | null;
  priceAnnual: number | null;
  description: string;
  features: FeatureCode[];
  quotas: PlanQuotas;
}

export const PLANS: PlanDef[] = [
  {
    id: 'free',
    serverId: 0,
    label: 'Free Trial',
    color: '#f97316',
    priceMonthly: 0,
    priceAnnual: 0,
    description: '30 хоногийн туршилт',
    quotas: {
      maxCompanies: 1,
      maxLocations: 1,
      maxUsers: 2,
      maxItems: 100,
      maxPosTxnsPerMonth: 200,
      trialDays: 30,
      auditRetentionDays: 7,
    },
    features: ['pos', 'inventory.basic', 'inventory.purchase', 'inventory.sale', 'report.basic'],
  },
  {
    id: 'starter',
    serverId: 1,
    label: 'Starter',
    color: '#3b82f6',
    priceMonthly: 49000,
    priceAnnual: 490000,
    description: 'Free Trial-тай ижил, тасралтгүй хэрэглэх',
    quotas: {
      maxCompanies: 1,
      maxLocations: 1,
      maxUsers: 2,
      maxItems: 500,
      maxPosTxnsPerMonth: -1,
      auditRetentionDays: 30,
    },
    features: ['pos', 'inventory.basic', 'inventory.purchase', 'inventory.sale', 'report.basic', 'ebarimt'],
  },
  {
    id: 'gold',
    serverId: 2,
    label: 'Gold',
    color: '#f59e0b',
    priceMonthly: 99000,
    priceAnnual: 990000,
    description: 'Өргөжиж буй бизнест тохиромжтой',
    quotas: {
      maxCompanies: 1,
      maxLocations: 3,
      maxUsers: 3,
      maxItems: -1,
      maxPosTxnsPerMonth: -1,
      auditRetentionDays: 90,
    },
    features: [
      'pos', 'inventory.basic', 'inventory.purchase', 'inventory.sale', 'inventory.stockcount',
      'report.basic', 'report.full', 'ebarimt', 'ar', 'ap', 'multilocation', 'support.chat',
      'shop.b2b',
    ],
  },
];

export const FEATURE_LABELS: Record<FeatureCode, string> = {
  'pos': 'ПОС касс',
  'inventory.basic': 'Бараа бүртгэл',
  'inventory.purchase': 'Худалдан авалт',
  'inventory.sale': 'Борлуулалт',
  'inventory.stockcount': 'Тооллого',
  'report.basic': 'Үндсэн тайлан',
  'report.full': 'Нарийвчилсан тайлан',
  'ebarimt': 'И-Баримт',
  'ar': 'Авлага',
  'ap': 'Өглөг',
  'multilocation': 'Олон салбар',
  'support.chat': 'Чат дэмжлэг',
  'shop.b2b': 'Онлайн дэлгүүр (бөөний апп)',
};

const SERVER_NAME_TO_ID: Record<string, PlanId> = {
  FreeTrial: 'free',
  Starter: 'starter',
  Gold: 'gold',
};

/** Plan-ыг олон төрлийн оролтоор normalize. backend нь string ('Gold') эсвэл int (2) буцаана. */
export function normalizePlan(input: unknown): PlanId {
  if (typeof input === 'number') {
    const found = PLANS.find((p) => p.serverId === input);
    return found?.id ?? 'free';
  }
  if (typeof input === 'string') {
    if (SERVER_NAME_TO_ID[input]) return SERVER_NAME_TO_ID[input];
    if ((PLANS.find((p) => p.id === input))) return input as PlanId;
  }
  return 'free';
}

export function planById(id: PlanId): PlanDef {
  return PLANS.find((p) => p.id === id) ?? PLANS[0]!;
}

export function planHasFeature(id: PlanId, feature: FeatureCode): boolean {
  return planById(id).features.includes(feature);
}

export function featureLabel(code: FeatureCode | string): string {
  return (FEATURE_LABELS as Record<string, string>)[code] ?? code;
}

export function quotaText(value: number): string {
  return value === -1 ? 'Хязгааргүй' : value.toLocaleString('mn-MN');
}
